import { t } from 'i18next'

import { countries } from '@app/consts/Countries'

export enum AddressEntryMode {
  Lookup = 'lookup',
  Selected = 'selected',
  Manual = 'manual',
}

interface AddressFormValues {
  address?: string
  primaryAddressCity?: string
  primaryAddressState?: string
  primaryAddressPostalCode?: string
  streetHouseNumber?: string
  addressEntryMode?: AddressEntryMode
}

interface AddressValues {
  address: string
  city: string
  state: string
  postalCode: string
  streetHouseNumber: string
}

type AddressValueKey = keyof AddressValues

interface AddressFieldValidationOptions {
  invalidMessageKey?: string
  pattern?: RegExp
}

interface AddressPostalCodeValidationOptions extends AddressFieldValidationOptions {
  maxLength?: number
}

interface StreetHouseNumberValidationOptions extends AddressFieldValidationOptions {
  streetHouseNumberNotAvailable?: boolean
  maxLength?: number
}

interface ManualAddressValidationOptions {
  address?: AddressFieldValidationOptions
  city?: AddressFieldValidationOptions
  state?: AddressFieldValidationOptions
  postalCode?: AddressPostalCodeValidationOptions
  streetHouseNumber?: StreetHouseNumberValidationOptions
}

const AddressLettersPattern = /^[A-Za-z0-9\s-.,&#/]*$/
const CityStatePattern = /^[A-Za-z0-9\s]+$/
const ZipCodePattern = /^[A-Za-z0-9\s-]+$/

export const ADDRESS_LOOKUP_DEBOUNCE_MS = 500

export const normalizeAddressValue = (value?: string | null): string => value || ''

export const getAddressRegionCode = (countryId?: string): string | undefined => {
  if (!countryId) return undefined

  return countries
    .find((country) => country.id.toLowerCase() === countryId.toLowerCase())
    ?.shortName.toLowerCase()
}

const hasCompletedAddressValues = (values?: AddressFormValues): boolean => {
  if (!values) return false

  return Boolean(
    values.address?.trim() ||
      values.primaryAddressCity?.trim() ||
      values.primaryAddressState?.trim() ||
      values.primaryAddressPostalCode?.trim() ||
      values.streetHouseNumber?.trim()
  )
}

export const getInitialAddressEntryMode = (values?: AddressFormValues): AddressEntryMode => {
  return (
    values?.addressEntryMode ||
    (hasCompletedAddressValues(values) ? AddressEntryMode.Manual : AddressEntryMode.Lookup)
  )
}

export const validateAddressLine = (
  value?: string,
  {
    invalidMessageKey = 'Validation.Address Line contains unsupported characters',
    pattern = AddressLettersPattern,
  }: AddressFieldValidationOptions = {}
): string | undefined => {
  const normalizedValue = normalizeAddressValue(value)

  if (!normalizedValue.trim()) {
    return t('Validation.Required')
  }

  if (!pattern.test(normalizedValue)) {
    return t(invalidMessageKey)
  }
}

export const validateAddressCity = (
  value?: string,
  {
    invalidMessageKey = 'Validation.Invalid city',
    pattern = CityStatePattern,
  }: AddressFieldValidationOptions = {}
): string | undefined => {
  const normalizedValue = normalizeAddressValue(value)

  if (!normalizedValue.trim()) {
    return t('Validation.Required')
  }

  if (!pattern.test(normalizedValue)) {
    return t(invalidMessageKey)
  }
}

export const validateAddressState = (
  value?: string,
  {
    invalidMessageKey = 'Validation.Invalid state',
    pattern = CityStatePattern,
  }: AddressFieldValidationOptions = {}
): string | undefined => {
  const normalizedValue = normalizeAddressValue(value)

  if (!normalizedValue.trim()) {
    return t('Validation.Required')
  }

  if (!pattern.test(normalizedValue)) {
    return t(invalidMessageKey)
  }
}

export const validateAddressPostalCode = (
  value?: string,
  {
    invalidMessageKey = 'Validation.Invalid postal code',
    maxLength = 32,
    pattern = ZipCodePattern,
  }: AddressPostalCodeValidationOptions = {}
): string | undefined => {
  const normalizedValue = normalizeAddressValue(value)

  if (!normalizedValue.trim()) {
    return t('Validation.Required')
  }

  if (normalizedValue.length > maxLength) {
    return t('Validation.Maximum character limit is 32')
  }

  if (!pattern.test(normalizedValue)) {
    return t(invalidMessageKey)
  }
}

export const validateStreetHouseNumber = (
  value: string | undefined,
  {
    invalidMessageKey = 'Validation.Address Line contains unsupported characters',
    maxLength = 20,
    pattern = AddressLettersPattern,
    streetHouseNumberNotAvailable = false,
  }: StreetHouseNumberValidationOptions = {}
): string | undefined => {
  const normalizedValue = normalizeAddressValue(value)

  if (!streetHouseNumberNotAvailable && !normalizedValue.trim()) {
    return t('Validation.Required')
  }

  if (streetHouseNumberNotAvailable) {
    return undefined
  }

  if (normalizedValue && !pattern.test(normalizedValue)) {
    return t(invalidMessageKey)
  }

  if (normalizedValue.length > maxLength) {
    return t('Validation.Max {{amount}} characters allowed', {
      amount: maxLength,
    })
  }
}

const validateManualAddress = (
  values: Partial<AddressValues>,
  options?: ManualAddressValidationOptions
): Partial<AddressValues> => {
  return {
    address: validateAddressLine(values.address, options?.address),
    city: validateAddressCity(values.city, options?.city),
    state: validateAddressState(values.state, options?.state),
    postalCode: validateAddressPostalCode(values.postalCode, options?.postalCode),
    streetHouseNumber: validateStreetHouseNumber(
      values.streetHouseNumber,
      options?.streetHouseNumber
    ),
  }
}

const getAddressFieldValue = (value: unknown): string | undefined => {
  return typeof value === 'string' ? value : undefined
}

export const validateManualAddressFields = <Values extends object>(
  values: Values,
  fieldMap: Record<AddressValueKey, Extract<keyof Values, string>>,
  options?: ManualAddressValidationOptions
): Partial<Record<Extract<keyof Values, string>, string>> => {
  const addressErrors = validateManualAddress(
    {
      address: getAddressFieldValue(values[fieldMap.address]),
      city: getAddressFieldValue(values[fieldMap.city]),
      state: getAddressFieldValue(values[fieldMap.state]),
      postalCode: getAddressFieldValue(values[fieldMap.postalCode]),
      streetHouseNumber: getAddressFieldValue(values[fieldMap.streetHouseNumber]),
    },
    options
  )
  const mappedErrors: Partial<Record<Extract<keyof Values, string>, string>> = {}

  if (addressErrors.address) {
    mappedErrors[fieldMap.address] = addressErrors.address
  }
  if (addressErrors.city) {
    mappedErrors[fieldMap.city] = addressErrors.city
  }
  if (addressErrors.state) {
    mappedErrors[fieldMap.state] = addressErrors.state
  }
  if (addressErrors.postalCode) {
    mappedErrors[fieldMap.postalCode] = addressErrors.postalCode
  }
  if (addressErrors.streetHouseNumber) {
    mappedErrors[fieldMap.streetHouseNumber] = addressErrors.streetHouseNumber
  }

  return mappedErrors
}
